/**
 * useCommands Hook
 * Handles slash commands for the CLI
 * Includes model switching, compact mode, session management, stats, and memory
 */

import { useCallback, useState, useRef, type MutableRefObject } from 'react';

export interface CommandResult {
    handled: boolean;
    output?: string;
    action?: 'exit' | 'clear' | 'toggle-raw' | 'toggle-debug' | 'toggle-compact' | 'toggle-gateway' | 'toggle-heartbeat';
}

export interface BackgroundServiceStatus {
    gatewayOnline: boolean;
    gatewayCompleted: number;
    heartbeatActive: boolean;
    heartbeatLastPoll?: string;
    heartbeatTriggered: number;
}

/** Session-level statistics exposed to commands and UI */
export interface SessionStats {
    startedAt: number;
    messageCount: number;
    toolCallCount: number;
    filesModified: Set<string>;
    tokensEstimate: number;
}

export interface UseCommandsOptions {
    mcpServers?: Array<{ name: string; tools?: Array<{ name: string }> }>;
    conversationId?: string;
    serviceStatusRef?: MutableRefObject<BackgroundServiceStatus>;
}

export interface UseCommandsReturn {
    rawMode: boolean;
    debugMode: boolean;
    compactMode: boolean;
    heartbeatMode: boolean;
    sessionStats: SessionStats;
    handleCommand: (input: string) => CommandResult;
    getHelp: () => string;
    getMcpStatus: () => string;
    getCompletions: (partial: string) => string[];
    trackToolCall: (toolName: string, filePath?: string) => void;
    trackTokens: (count: number) => void;
}

export function useCommands(options: UseCommandsOptions = {}): UseCommandsReturn {
    const { mcpServers = [] } = options;

    const [rawMode, setRawMode] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    const [compactMode, setCompactMode] = useState(false);
    const [heartbeatMode, setHeartbeatMode] = useState(true);

    // Session stats tracking
    const statsRef = useRef<SessionStats>({
        startedAt: Date.now(),
        messageCount: 0,
        toolCallCount: 0,
        filesModified: new Set(),
        tokensEstimate: 0,
    });

    const trackToolCall = useCallback((toolName: string, filePath?: string) => {
        statsRef.current.toolCallCount++;
        if (filePath && (toolName === 'paean_edit_file' || toolName === 'paean_write_file')) {
            statsRef.current.filesModified.add(filePath);
        }
    }, []);

    const trackTokens = useCallback((count: number) => {
        statsRef.current.tokensEstimate += count;
    }, []);

    const COMMANDS = [
        '/exit', '/quit', '/q',
        '/clear', '/cls',
        '/help', '/h', '/?',
        '/debug',
        '/mcp',
        '/raw',
        '/compact',
        '/model',
        '/status',
        '/stats',
        '/history',
        '/gateway',
        '/heartbeat',
        '/memory',
        '/shortcuts',
        '/tools',
        '/loop',
        '/usage',
    ];

    const getHelp = useCallback(() => {
        return `
  ─── Commands ───────────────────────────────────────

    /help        Show this help (/h, /?)
    /exit        Exit the chat session (/quit, /q)
    /clear       Clear the screen (/cls)
    /compact     Toggle compact mode (hide tool details)
    /raw         Toggle raw output (no markdown)
    /debug       Toggle debug mode

  ─── Info ───────────────────────────────────────────

    /status      Show session status & config
    /stats       Show detailed session statistics
    /usage       Show subscription, credits, and 5h/7d usage
    /mcp         Show MCP connection status
    /tools       List all available local tools
    /model       Show current model info
    /memory      Show saved memories for this project
    /history     Show conversation history commands

  ─── Automation ─────────────────────────────────────

    /loop <desc> Set up a recurring loop task (natural language)
                 e.g. /loop check git status every 5 minutes

  ─── Services ───────────────────────────────────────

    /gateway     Toggle gateway relay on/off
    /heartbeat   Toggle cloud schedule auto-polling

  ─── Shortcuts ──────────────────────────────────────

    /shortcuts   Show all keyboard shortcuts

  Tip: Type / and press Tab to autocomplete
`;
    }, []);

    const getMcpStatus = useCallback(() => {
        if (mcpServers.length === 0) {
            return '\n  No MCP servers connected\n';
        }

        let status = '\n  MCP Connections:\n\n';
        for (const server of mcpServers) {
            const toolCount = server.tools?.length || 0;
            const connStatus = toolCount > 0 ? 'connected' : 'pending';
            status += `    ${server.name}: ${toolCount} tools (${connStatus})\n`;
        }

        const totalTools = mcpServers.reduce((sum, s) => sum + (s.tools?.length || 0), 0);
        status += `\n    Total: ${totalTools} tools across ${mcpServers.length} server(s)\n`;
        return status;
    }, [mcpServers]);

    const getCompletions = useCallback((partial: string): string[] => {
        if (!partial.startsWith('/')) {
            return [];
        }
        const lower = partial.toLowerCase();
        return COMMANDS.filter(cmd => cmd.startsWith(lower));
    }, []);

    const handleCommand = useCallback((input: string): CommandResult => {
        const parts = input.trim().split(/\s+/);
        const baseCmd = parts[0].toLowerCase();

        switch (baseCmd) {
            case '/exit':
            case '/quit':
            case '/q':
                return { handled: true, action: 'exit' };

            case '/clear':
            case '/cls':
                return { handled: true, action: 'clear' };

            case '/help':
            case '/h':
            case '/?':
                return { handled: true, output: getHelp() };

            case '/debug': {
                let toggled = false;
                setDebugMode(prev => { toggled = !prev; return !prev; });
                return {
                    handled: true,
                    output: `  Debug mode: ${toggled ? 'ON' : 'OFF'}`,
                    action: 'toggle-debug'
                };
            }

            case '/raw': {
                let toggled = false;
                setRawMode(prev => { toggled = !prev; return !prev; });
                let rawOutput = `  Raw mode: ${toggled ? 'ON' : 'OFF'}`;
                if (toggled) {
                    rawOutput += '\n  Agent will return plain text (no markdown)';
                }
                return {
                    handled: true,
                    output: rawOutput,
                    action: 'toggle-raw'
                };
            }

            case '/compact': {
                let toggled = false;
                setCompactMode(prev => { toggled = !prev; return !prev; });
                return {
                    handled: true,
                    output: `  Compact mode: ${toggled ? 'ON' : 'OFF'}${toggled ? '\n  Tool call details will be hidden' : ''}`,
                    action: 'toggle-compact'
                };
            }

            case '/mcp':
                return { handled: true, output: getMcpStatus() };

            case '/model':
                return {
                    handled: true,
                    output: '\n  Model: Paean AI (cloud)\n  Selection is handled server-side based on task complexity.\n'
                };

            case '/gateway': {
                const gwStatus = options.serviceStatusRef?.current;
                if (gwStatus?.gatewayOnline) {
                    return { handled: true, output: '  Gateway: turning OFF', action: 'toggle-gateway' };
                }
                return { handled: true, output: '  Gateway: turning ON — remote clients can connect', action: 'toggle-gateway' };
            }

            case '/heartbeat': {
                let toggled = false;
                setHeartbeatMode(prev => { toggled = !prev; return !prev; });
                return {
                    handled: true,
                    output: toggled
                        ? '  Cloud task polling: ON (every 5m)'
                        : '  Cloud task polling: OFF',
                    action: 'toggle-heartbeat'
                };
            }

            case '/status': {
                const s = statsRef.current;
                const elapsed = Date.now() - s.startedAt;
                const mins = Math.floor(elapsed / 60000);
                const secs = Math.floor((elapsed % 60000) / 1000);
                const duration = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                const convId = options.conversationId;
                const svc = options.serviceStatusRef?.current;

                let status = '\n  ─── Session Status ─────────────────────\n\n';
                status += `    Duration:       ${duration}\n`;
                status += `    Messages:       ${s.messageCount}\n`;
                status += `    Tool calls:     ${s.toolCallCount}\n`;
                status += `    Files modified: ${s.filesModified.size}\n`;
                if (convId) {
                    status += `    Conversation:   ${convId.substring(0, 8)}...\n`;
                }
                status += `    CWD:            ${process.cwd()}\n`;
                status += '\n  ─── Config ─────────────────────────────\n\n';
                status += `    Raw mode:       ${rawMode ? 'ON' : 'OFF'}\n`;
                status += `    Compact mode:   ${compactMode ? 'ON' : 'OFF'}\n`;
                status += `    Debug mode:     ${debugMode ? 'ON' : 'OFF'}\n`;
                status += `    MCP servers:    ${mcpServers.length}\n`;
                status += `    Gateway:        ${svc?.gatewayOnline ? 'online' : 'offline'}`;
                if ((svc?.gatewayCompleted ?? 0) > 0) {
                    status += ` (${svc!.gatewayCompleted} completed)`;
                }
                status += '\n';
                status += `    Heartbeat:      ${svc?.heartbeatActive ? 'ON' : 'OFF'}`;
                if (svc?.heartbeatActive && svc?.heartbeatLastPoll) {
                    status += ` (last: ${svc.heartbeatLastPoll})`;
                }
                status += '\n';
                return { handled: true, output: status };
            }

            case '/stats': {
                const s = statsRef.current;
                const elapsed = Date.now() - s.startedAt;
                const mins = Math.floor(elapsed / 60000);
                const secs = Math.floor((elapsed % 60000) / 1000);
                const duration = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

                let out = '\n  ─── Session Statistics ─────────────────\n\n';
                out += `    Session duration:  ${duration}\n`;
                out += `    Messages sent:     ${s.messageCount}\n`;
                out += `    Tool calls made:   ${s.toolCallCount}\n`;
                out += `    Files modified:    ${s.filesModified.size}\n`;
                if (s.filesModified.size > 0) {
                    out += '\n    Modified files:\n';
                    for (const f of s.filesModified) {
                        out += `      ${f}\n`;
                    }
                }
                if (s.tokensEstimate > 0) {
                    out += `\n    Est. tokens:       ~${s.tokensEstimate.toLocaleString()}\n`;
                }
                out += '\n';
                return { handled: true, output: out };
            }

            case '/history':
                return {
                    handled: true,
                    output: '\n  Conversation History:\n\n    List:   paean ls\n    Resume: paean resume <id>\n    Show:   paean show <id>\n    Star:   paean star <id>\n'
                };

            case '/memory': {
                let out = '\n  Loading memories...\n';
                // Synchronous check for memory file
                const memoryPath = `${process.cwd()}/.paean/PAEAN.md`;
                try {
                    const fs = require('fs');
                    if (fs.existsSync(memoryPath)) {
                        const content = fs.readFileSync(memoryPath, 'utf-8') as string;
                        const count = (content.match(/^- /gm) || []).length;
                        out = `\n  ─── Paean Memories (${count}) ─────────────\n  Path: ${memoryPath}\n\n`;
                        const lines = content.split('\n');
                        for (const line of lines) {
                            if (line.startsWith('- ')) {
                                out += `    ${line}\n`;
                            }
                        }
                        if (count === 0) out += '    (no memories saved yet)\n';
                        out += '\n  Tip: Ask the agent to "remember that..." to save memories\n';
                    } else {
                        out = '\n  No memories saved yet.\n  Ask the agent to "remember that..." to save facts for future sessions.\n';
                    }
                } catch {
                    out = '\n  No memories saved yet.\n  Ask the agent to "remember that..." to save facts for future sessions.\n';
                }
                return { handled: true, output: out };
            }

            case '/shortcuts':
                return {
                    handled: true,
                    output: `
  ─── Keyboard Shortcuts ────────────────────────

    Navigation
      Up/Down         Navigate input history
      Left/Right      Move cursor in input
      Ctrl+A          Jump to start of line
      Ctrl+E          Jump to end of line

    Editing
      Ctrl+U          Clear line before cursor
      Ctrl+K          Clear line after cursor
      Ctrl+W          Delete word before cursor
      Esc             Clear entire input
      \\+Enter         Multi-line continuation

    Commands
      Tab             Autocomplete slash commands
      Ctrl+C          Abort current request / exit
      Ctrl+C Ctrl+C   Force exit (during processing)

`
                };

            case '/tools': {
                const totalTools = mcpServers.reduce((sum, s) => sum + (s.tools?.length || 0), 0);
                let out = `\n  ─── Available Tools (${totalTools}) ─────────────\n\n`;

                for (const server of mcpServers) {
                    const tools = server.tools || [];
                    out += `  ${server.name} (${tools.length} tools)\n`;
                    for (const tool of tools.slice(0, 30)) {
                        out += `    ${tool.name}\n`;
                    }
                    if (tools.length > 30) {
                        out += `    ... and ${tools.length - 30} more\n`;
                    }
                    out += '\n';
                }
                return { handled: true, output: out };
            }

            case '/loop': {
                const loopDesc = parts.slice(1).join(' ').trim();
                if (!loopDesc) {
                    return {
                        handled: true,
                        output: '  Usage: /loop <description>\n  e.g. /loop check git status every 5 minutes\n  e.g. /loop run tests every 30 seconds\n  e.g. /loop monitor disk usage every hour',
                    };
                }
                statsRef.current.messageCount++;
                return { handled: false };
            }

            default:
                if (baseCmd.startsWith('/')) {
                    return {
                        handled: true,
                        output: `\n  Unknown command: ${baseCmd}\n  Type /help for available commands\n`
                    };
                }

                statsRef.current.messageCount++;
                return { handled: false };
        }
    }, [getHelp, getMcpStatus, mcpServers.length, options.conversationId, rawMode, compactMode, debugMode]);

    return {
        rawMode,
        debugMode,
        compactMode,
        heartbeatMode,
        sessionStats: statsRef.current,
        handleCommand,
        getHelp,
        getMcpStatus,
        getCompletions,
        trackToolCall,
        trackTokens,
    };
}
